/**
 * AlertsCard.js — Dashboard alert card with expand/collapse
 *
 * Displays recent spending alerts from Cloud Functions analysis.
 * Collapsed: unread count + most recent alert body.
 * Expanded: list of up to 10 alerts with colored type dots, title, body, relative time.
 *
 * Returns null if no alerts exist (card is hidden, not empty).
 *
 * Props:
 *   alerts        — Array of alert objects from Firestore
 *   onMarkRead    — Callback(alertId) to mark single alert as read
 *   onMarkAllRead — Callback() to mark all alerts as read
 */
import React, { useState } from 'react';

/** Map alert type to dot color class */
const TYPE_DOT = {
  budget_exceeded: 'alert-dot--danger',
  money_on_fire: 'alert-dot--danger',
  pace_warning: 'alert-dot--warning',
  overspend_projection: 'alert-dot--warning',
  category_spike: 'alert-dot--info',
  savings_rate: 'alert-dot--success',
};

/**
 * Format a Firestore timestamp or Date to relative time string.
 * @param {Object|Date} ts - Firestore Timestamp or Date
 * @returns {string} Relative time (e.g., "2h ago", "3d ago")
 */
function relativeTime(ts) {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function AlertsCard({ alerts = [], onMarkRead, onMarkAllRead }) {
  const [expanded, setExpanded] = useState(false);

  if (!alerts || alerts.length === 0) return null;

  const unreadCount = alerts.filter(a => !a.read).length;
  const newest = alerts[0];

  return (
    <div className="alerts-card">
      {/* Header */}
      <div className="alerts-header" onClick={() => setExpanded(!expanded)}>
        <h3>Alerts</h3>
        {unreadCount > 0 && (
          <span className="alerts-badge">{unreadCount}</span>
        )}
        <span className="alerts-chevron">{expanded ? '\u25B2' : '\u25BC'}</span>
      </div>

      {/* Collapsed preview */}
      {!expanded && newest && (
        <div className="alerts-preview">
          {newest.body}
        </div>
      )}

      {/* Expanded list */}
      {expanded && (
        <div style={{ marginTop: '0.5rem' }}>
          {alerts.map(alert => (
            <div
              key={alert.id}
              className={`alert-row ${alert.read ? 'alert-row--read' : ''}`}
              onClick={() => !alert.read && onMarkRead?.(alert.id)}
            >
              <div className={`alert-dot ${TYPE_DOT[alert.type] || 'alert-dot--warning'}`} />
              <div className="alert-info">
                <div className="alert-title">{alert.title}</div>
                <div className="alert-body">{alert.body}</div>
                <div className="alert-time">{relativeTime(alert.created_at)}</div>
              </div>
            </div>
          ))}

          {/* Footer */}
          {unreadCount > 0 && (
            <div className="alerts-footer">
              <button
                type="button"
                style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); onMarkAllRead?.(); }}
              >
                Mark all read
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
